import { Avatar } from 'antd';
import { FC } from 'react';
import dayjs from 'dayjs';
import { PaymentRequestForm } from './embedded/payment-request-form';
import { RequestFeedbackForm } from './embedded/request-feedback-form';
import { SendRefundForm } from './embedded/send-refund-form';
import DOMPurify from 'dompurify';
import { PaymentInstrument } from '../../../generated';
import { Country } from '../../../types/country';
import { DATE_TIME_FORMAT } from '../../../constants';
import intealthLogo from '../../../assets/intealth-logo.png';
import utc from 'dayjs/plugin/utc';
import { PaymentRejectedForm } from './embedded/payment-rejected-form';
import { PaymentFailedForm } from './embedded/payment-failed-form';
import { PaymentCompletedForm } from './embedded/payment-completed-form';
dayjs.extend(utc);
interface ChatMessageProps {
  cyberSourcePublicKey: string;
  applicantId: string;
  paymentInstrument: PaymentInstrument;
  country: Country[];
  id: string;
  sentBy: string;
  message: string;
  embedding: string | null | undefined;
  createdAt: string;
  isAdmin: boolean;
  onSave: (data: { id: string; messages: { id: string; embedding: string; }[] }) => Promise<void>;
  startPollingMessages: (interval: number) => void;
  pollingMessagesData: { data: { id: string, messages: { id?: string; sentBy?: string; message?: string; embedding?: string }[] }};
  stopPollingMessages: () => void;
  caseId: string;
  lastName: string;
  restOfName: string;
  transactions: any;
}

export const ChatMessage: FC<ChatMessageProps> = (props) => {
  const caseWorkerStyles = {
    background: '#edeeef',
    padding: '8px',
    color: 'black',
    borderRadius: '8px',
    fontWeight: '12px',
    whiteSpace: 'pre-wrap',
    maxWidth: '85%',
    display: 'inline-block'
  };

  const applicantStyles = {
    background: '#d4d8f7',
    padding: '8px',
    color: 'black',
    borderRadius: '8px',
    fontWeight: '12px',
    whiteSpace: 'pre-wrap',
    maxWidth: '85%',
    display: 'inline-block'
  };

  const placeHolderInitials = `${props.restOfName.charAt(0)}${props.lastName.charAt(0)}`;

  const toggle = (props.sentBy === 'internal' && props.isAdmin) || (props.sentBy === 'external' && !props.isAdmin);

  const getEmbeddedComponent = () => {
    const embeddedComponents: JSX.Element[] = [];
    if (!props.embedding) { return embeddedComponents; }

    const componentData: any[] = JSON.parse(props.embedding);

    const createComponent = (component: any) => {
      switch (component.value) {
        case 'documentRequestType':
          return (
            <RequestFeedbackForm
              key={'RequestFeedbackForm' + component.id}
              isAdmin={false}
              changesRequested={component.revisionRequest?.requestedChanges}
              onSave={props.onSave}
              caseId={props.caseId}
              messageId={props.id}
              completed={component.completed}
              checked={component.checked}
            />
          );
        case 'requestPayment':
          if (component.adhocTransactionId === null || component.adhocTransactionId === undefined) {
            const pendingTransaction = props.transactions?.adhocTransactions?.find(
              (transaction: any) =>
                transaction?.status === 'PENDING' &&
                transaction?.reason === component.adhocPayment?.reason &&
                transaction?.amount === component.adhocPayment?.amount
            );
            if (pendingTransaction) {
              component.adhocTransactionId = pendingTransaction?.id;
            }
          }
          return (
            <PaymentRequestForm
              key={'PaymentRequestForm' + component.id}
              message={{
                id: props.id,
                sentBy: props.sentBy,
                message: props.message
              }}
              reason={component.adhocPayment?.reason}
              amount={parseFloat(component.adhocPayment?.amount ?? '')}
              adhocPaymentType={component.adhocPayment?.adhocPaymentType}
              adhocTransactionId={component.adhocTransactionId}
              completed={component.completed}
              onSave={props.onSave}
              startPollingMessages={props.startPollingMessages}
              pollingMessagesData={props.pollingMessagesData}
              stopPollingMessages={props.stopPollingMessages}
              caseId={props.caseId}
              paymentInstrument={props?.paymentInstrument}
              applicantId={props?.applicantId}
              countries={props.country}
              cyberSourcePublicKey={props.cyberSourcePublicKey}
            />
          );
        case 'sendRefund':
          return (
            <SendRefundForm
              key={'SendRefundForm' + component.id}
              id={component.id}
              isAdmin={false}
              reason={component?.adhocPayment?.reason}
              amount={parseFloat(component?.adhocPayment?.amount ?? '0') * -1}
              transactions={props.transactions}
            />
          );
        case 'paymentRejected':
          return (
            <PaymentRejectedForm
              key={'PaymentRejectedForm' + component.id}
              amount={parseFloat(component?.adhocPayment?.amount ?? '')}
              reason={component?.adhocPayment?.reason}
              applicantRespondedAt={component?.applicantRespondedAt}
            />
          );
        case 'paymentFailed':
          return (
            <PaymentFailedForm
              key={'PaymentFailedForm' + component.id}
              amount={parseFloat(component?.adhocPayment?.amount ?? '')}
              reason={component?.adhocPayment?.reason}
              errorMessage={component?.errorMessage}
              errorOccurredAt={component?.errorOccurredAt}
            />
          );
        case 'paymentCompleted':
          return (
            <PaymentCompletedForm
              key={'PaymentCompletedForm' + component.id}
              adhocTransactionId={component.adhocTransactionId}
              amount={parseFloat(component.adhocPayment?.amount ?? '')}
              adhocTransactions={props?.transactions?.adhocTransactions}
            />
          );
        default:
          return null;
      }
    };

    for (const component of componentData) {
      const embeddedComponent = createComponent(component);
      if (embeddedComponent) {
        embeddedComponents.push(embeddedComponent);
      }
    }

    return embeddedComponents;
  };

  return (
    <div
      key={props.id}
      style={{
        display: 'flex',
        width: '100%',
        justifyContent: toggle ? 'flex-start' : 'flex-end',
        padding: '0 10px'
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: toggle ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          width: '100%'
        }}
      >
        <div
          style={{
            marginRight: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'translateY(10px)'
          }}
        >
          <Avatar
            style={{
              marginLeft: 5,
              backgroundColor: 'white',
              color: 'black',
              border: '1px solid black'
            }}
            size="large"
          >
            {props.sentBy === 'internal' ? <img src={intealthLogo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Intealth" /> : placeHolderInitials}
          </Avatar>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: toggle ? 'flex-end' : 'flex-start',
            width: '100%'
          }}
        >
          <div
            style={{
              color: 'grey',
              fontSize: '10px',
              textAlign: toggle ? 'right' : 'left',
              marginBottom: '4px'
            }}
          >
            {dayjs(props.createdAt).utc().format(DATE_TIME_FORMAT).toString().toUpperCase()}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: toggle ? 'flex-end' : 'flex-start',
              width: '100%'
            }}
          >
            <div
              style={{
                ...(toggle ? applicantStyles : caseWorkerStyles),
                marginLeft: toggle ? 'auto' : '0',
                marginRight: toggle ? '0' : 'auto',
                maxWidth: '85%',
                textAlign: 'left',
                marginBottom: '20px'
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(props.message) }} /> {getEmbeddedComponent().map((component) => component)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
